import type { GuideTourAction, GuideTourRuntimeState } from './types'
import {
  findNextAvailableGuideTourStepIndex,
  findPreviousAvailableGuideTourStepIndex,
} from './utils/tourRuntime'
import { createInitialGuideTourState } from './stateInitial'

const reduceGuideTourNext = (
  state: GuideTourRuntimeState
): GuideTourRuntimeState => {
  if (!state.activeTour) {
    return state
  }

  const nextStepIndex = findNextAvailableGuideTourStepIndex(
    state.activeTour,
    state.currentStepIndex + 1
  )

  if (nextStepIndex === null) {
    if (state.status === 'active' && state.activeTour) {
      return {
        ...state,
        status: 'completed',
      }
    }

    return state
  }

  return {
    ...state,
    currentStepIndex: nextStepIndex,
  }
}

const reduceGuideTourPrev = (
  state: GuideTourRuntimeState
): GuideTourRuntimeState => {
  if (!state.activeTour) {
    return state
  }

  const previousStepIndex = findPreviousAvailableGuideTourStepIndex(
    state.activeTour,
    state.currentStepIndex - 1
  )

  if (previousStepIndex === null) {
    return state
  }

  return {
    ...state,
    currentStepIndex: previousStepIndex,
  }
}

export const applyGuideTourReducerAction = (
  state: GuideTourRuntimeState,
  action: GuideTourAction
): GuideTourRuntimeState => {
  switch (action.type) {
    case 'START':
      return {
        status: 'active',
        activeTourId: action.tour.id,
        activeTour: action.tour,
        currentStepIndex: action.stepIndex ?? 0,
        viewedStepCount: action.viewedStepCount ?? 0,
      }
    case 'NEXT':
      return reduceGuideTourNext(state)
    case 'PREV':
      return reduceGuideTourPrev(state)
    case 'SKIP':
      if (state.status !== 'active' || !state.activeTour) {
        return state
      }

      return {
        ...state,
        status: 'skipped',
      }
    case 'DISMISS':
      return createInitialGuideTourState()
    case 'COMPLETE':
      if (state.status !== 'active' || !state.activeTour) {
        return state
      }

      return {
        ...state,
        status: 'completed',
      }
    case 'MARK_STEP_VIEWED':
      return {
        ...state,
        viewedStepCount: state.viewedStepCount + 1,
      }
    case 'GO_TO_STEP':
      if (
        state.status !== 'active' ||
        !state.activeTour ||
        action.stepIndex < 0 ||
        action.stepIndex >= state.activeTour.steps.length
      ) {
        return state
      }

      return {
        ...state,
        currentStepIndex: action.stepIndex,
      }
    case 'RESET':
      return createInitialGuideTourState()
    default:
      return state
  }
}
